import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProperties, 
  selectProperties, 
  selectPropertyLoading, 
  selectPropertyError, 
  selectPropertyPagination,
  selectFilters,
  setFilters,
  clearFilters 
} from '../redux/slices/propertySlice';

const Properties = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const properties = useSelector(selectProperties);
  const loading = useSelector(selectPropertyLoading);
  const error = useSelector(selectPropertyError);
  const pagination = useSelector(selectPropertyPagination);
  const filters = useSelector(selectFilters);
  
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false); // NEW: Mobile filter toggle

  // Initialize filters from URL params
  useEffect(() => {
    const urlFilters = {
      propertyType: searchParams.get('propertyType') || '',
      city: searchParams.get('city') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      bedrooms: searchParams.get('bedrooms') || '',
      bathrooms: searchParams.get('bathrooms') || '',
      balcony: searchParams.get('balcony') || '',
      status: searchParams.get('status') || '',
      sortBy: searchParams.get('sortBy') || 'date-desc',
      minArea: searchParams.get('minArea') || '',
      maxArea: searchParams.get('maxArea') || '',
      yearBuilt: searchParams.get('yearBuilt') || '',
      parkingSpaces: searchParams.get('parkingSpaces') || '',
      furnishing: searchParams.get('furnishing') || '',
      keyword: searchParams.get('keyword') || '',
      builder: searchParams.get('builder') || '',
      propertyCategory: searchParams.get('propertyCategory') || '',
      projectStatus: searchParams.get('projectStatus') || '',
      doorFacing: searchParams.get('doorFacing') || '',
      katha: searchParams.get('katha') || '',
      floorNo: searchParams.get('floorNo') || '',
      minTotalFloors: searchParams.get('minTotalFloors') || '',
      maxTotalFloors: searchParams.get('maxTotalFloors') || '',
      serventRoom: searchParams.get('serventRoom') || '',
      page: searchParams.get('page') || '1'
    };
    
    const hasUrlParams = Object.values(urlFilters).some(value => value !== '' && value !== '1');
    if (hasUrlParams) {
      dispatch(setFilters(urlFilters));
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    dispatch(fetchProperties(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { 
      ...filters, 
      [key]: value, 
      page: '1'
    };
    
    dispatch(setFilters(newFilters));
    updateSearchParams(newFilters);
  };

  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) params.set(key, newFilters[key]);
    });
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const newFilters = { 
      ...filters, 
      page: page.toString() 
    };
    
    dispatch(setFilters(newFilters));
    updateSearchParams(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setSearchParams(new URLSearchParams());
    setShowMobileFilters(false); // Close mobile filters after clearing
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatArea = (area) => {
    if (!area) return 'N/A';
    return `${parseFloat(area).toLocaleString('en-IN')} sq ft`;
  };

  const getDisplayArea = (property) => {
    if (property.carpetArea) return formatArea(property.carpetArea);
    if (property.superBuiltUpArea) return formatArea(property.superBuiltUpArea);
    if (property.squareFootage) return formatArea(property.squareFootage);
    return 'N/A';
  };

  // Property Card Component
  const PropertyCard = ({ property }) => {
    const getPropertyImage = (property) => {
      if (property.imageUrl) return property.imageUrl;
      
      if (property.images && property.images.length > 0) {
        const firstImage = property.images[0];
        if (typeof firstImage === 'object' && firstImage.url) {
          return firstImage.url;
        }
        if (typeof firstImage === 'string') {
          return firstImage.startsWith('http') 
            ? firstImage 
            : `${process.env.REACT_APP_API_URL}/uploads/properties/${firstImage}`;
        }
      }
      
      if (property.featuredImage) {
        return property.featuredImage.startsWith('http') 
          ? property.featuredImage 
          : `${process.env.REACT_APP_API_URL}/uploads/properties/${property.featuredImage}`;
      }
      
      return "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
    };

    return (
      <Link 
        to={`/properties/${property.id}`}
        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden"
      >
        <div className="relative overflow-hidden">
          <img 
            src={getPropertyImage(property)}
            alt={property.title}
            className="w-full h-48 sm:h-56 md:h-64 object-cover group-hover:scale-110 transition-transform duration-700"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
            }}
          />
          
          {/* Status Badge */}
          <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold text-white backdrop-blur-sm ${
              property.status === 'available' ? 'bg-green-500/90' : 
              property.status === 'sold' ? 'bg-red-500/90' :
              property.status === 'rented' ? 'bg-blue-500/90' : 'bg-yellow-500/90'
            }`}>
              {property.status}
            </span>
          </div>
          
          {/* Price Badge - MOBILE OPTIMIZED */}
          <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
            <span className="bg-black/70 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold">
              {formatPrice(property.price)}
            </span>
          </div>
          
          {/* Featured Badge */}
          {(property.isFeatured || property.featured) && (
            <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4">
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                ⭐ FEATURED
              </span>
            </div>
          )}

          {/* Property Type Badge */}
          <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4">
            <span className="bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded text-xs capitalize font-medium">
              {property.propertyType}
            </span>
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </div>
        
        <div className="p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold mb-2 text-black sm:mb-3 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
            {property.title}
          </h3>
          
          <p className="text-gray-600 mb-3 sm:mb-4 flex items-center text-xs sm:text-sm">
            <span className="mr-1">📍</span>
            <span className="line-clamp-1">{property.city}, {property.state}</span>
          </p>

          {/* Builder & Property Tags - MOBILE OPTIMIZED */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {property.builder && (
              <span className="bg-purple-50 text-purple-700 px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                🏗️ {property.builder}
              </span>
            )}
            {property.propertyCategory && (
              <span className="bg-yellow-50 text-yellow-700 px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                {property.propertyCategory}
              </span>
            )}
            {property.projectStatus && (
              <span className="bg-blue-50 text-blue-700 px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                {property.projectStatus}
              </span>
            )}
          </div>
          
          {/* Property Features Grid - MOBILE OPTIMIZED */}
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2 text-center text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
            <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
              <span className="text-base sm:text-lg mb-0.5 sm:mb-1">🛏️</span>
              <span className="font-semibold text-gray-900">{property.bedrooms || 0}</span>
              <span className="text-xs hidden sm:inline">Beds</span>
            </div>
            <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
              <span className="text-base sm:text-lg mb-0.5 sm:mb-1">🚿</span>
              <span className="font-semibold text-gray-900">{property.bathrooms || 0}</span>
              <span className="text-xs hidden sm:inline">Baths</span>
            </div>
            <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
              <span className="text-base sm:text-lg mb-0.5 sm:mb-1">🏡</span>
              <span className="font-semibold text-gray-900">{property.balcony || 0}</span>
              <span className="text-xs hidden sm:inline">Balcony</span>
            </div>
            <div className="flex flex-col items-center p-1.5 sm:p-2 bg-gray-50 rounded-lg">
              <span className="text-base sm:text-lg mb-0.5 sm:mb-1">🚗</span>
              <span className="font-semibold text-gray-900">{property.parkingSpaces || 0}</span>
              <span className="text-xs hidden sm:inline">Parking</span>
            </div>
          </div>

          {/* Area & Floor Information - MOBILE OPTIMIZED */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600">Area:</span>
                <span className="font-semibold text-gray-900 ml-1 text-xs sm:text-sm">{getDisplayArea(property)}</span>
              </div>
              {(property.floorNo || property.totalFloors) && (
                <div>
                  <span className="text-gray-600">Floor:</span>
                  <span className="font-semibold text-gray-900 ml-1">
                    {property.floorNo || 'N/A'}/{property.totalFloors || 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Tags - MOBILE OPTIMIZED */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {property.furnishing && (
              <span className="bg-green-50 text-green-700 px-2 py-0.5 sm:py-1 rounded text-xs font-medium">
                {property.furnishing}
              </span>
            )}
            {property.doorFacing && (
              <span className="bg-orange-50 text-orange-700 px-2 py-0.5 sm:py-1 rounded text-xs font-medium">
                🧭 {property.doorFacing}
              </span>
            )}
            {property.katha && (
              <span className="bg-pink-50 text-pink-700 px-2 py-0.5 sm:py-1 rounded text-xs font-medium">
                📄 {property.katha}
              </span>
            )}
          </div>

          {/* Year Built - MOBILE OPTIMIZED */}
          {property.yearBuilt && (
            <div className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 text-center">
              <span>🗓️ {property.yearBuilt}</span>
              <span className="mx-2">•</span>
              <span>{new Date().getFullYear() - parseInt(property.yearBuilt)} yrs</span>
            </div>
          )}

          {/* Owner Info - MOBILE OPTIMIZED */}
          
        </div>
      </Link>
    );
  };

  // Property List Item - MOBILE OPTIMIZED
  const PropertyListItem = ({ property }) => {
    const getPropertyImage = (property) => {
      if (property.imageUrl) return property.imageUrl;
      
      if (property.images && property.images.length > 0) {
        const firstImage = property.images[0];
        if (typeof firstImage === 'object' && firstImage.url) {
          return firstImage.url;
        }
        if (typeof firstImage === 'string') {
          return firstImage.startsWith('http') 
            ? firstImage 
            : `${process.env.REACT_APP_API_URL}/uploads/properties/${firstImage}`;
        }
      }
      
      if (property.featuredImage) {
        return property.featuredImage.startsWith('http') 
          ? property.featuredImage 
          : `${process.env.REACT_APP_API_URL}/uploads/properties/${property.featuredImage}`;
      }
      
      return "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
    };

    return (
      <Link 
        to={`/properties/${property.id}`}
        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col md:flex-row"
      >
        <div className="relative w-full md:w-80 flex-shrink-0 overflow-hidden">
          <img 
            src={getPropertyImage(property)}
            alt={property.title}
            className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
            }}
          />
          
          <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold text-white backdrop-blur-sm ${
              property.status === 'available' ? 'bg-green-500/90' : 
              property.status === 'sold' ? 'bg-red-500/90' :
              property.status === 'rented' ? 'bg-blue-500/90' : 'bg-yellow-500/90'
            }`}>
              {property.status}
            </span>
          </div>

          {(property.isFeatured || property.featured) && (
            <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4">
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-bold">
                ⭐ FEATURED
              </span>
            </div>
          )}
        </div>
        
        <div className="flex-1 p-4 sm:p-6 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-start mb-3 sm:mb-4">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-blue-600 transition-colors duration-300">
                {property.title}
              </h3>
              <p className="text-gray-600 flex items-center mb-2 sm:mb-3 text-sm sm:text-base">
                📍 {property.city}, {property.state}
              </p>
              
              {property.builder && (
                <p className="text-gray-700 font-medium mb-2 text-sm sm:text-base">
                  🏗️ Builder: <span className="text-blue-600">{property.builder}</span>
                </p>
              )}
            </div>
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 mt-2 md:mt-0">
              {formatPrice(property.price)}
            </div>
          </div>

          {/* Property Tags */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {property.propertyCategory && (
              <span className="bg-yellow-100 text-yellow-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                {property.propertyCategory}
              </span>
            )}
            {property.projectStatus && (
              <span className="bg-indigo-100 text-indigo-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                {property.projectStatus}
              </span>
            )}
            {property.furnishing && (
              <span className="bg-green-100 text-green-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                {property.furnishing}
              </span>
            )}
            {property.katha && (
              <span className="bg-pink-100 text-pink-800 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                {property.katha}
              </span>
            )}
          </div>
          
          {/* Property Features Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-lg sm:text-xl mb-1">🛏️</span>
              <span className="font-semibold text-sm sm:text-base">{property.bedrooms || 0}</span>
              <span className="text-xs text-gray-600">Beds</span>
            </div>
            <div className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-lg sm:text-xl mb-1">🚿</span>
              <span className="font-semibold text-sm sm:text-base">{property.bathrooms || 0}</span>
              <span className="text-xs text-gray-600">Baths</span>
            </div>
            <div className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-lg sm:text-xl mb-1">🏡</span>
              <span className="font-semibold text-sm sm:text-base">{property.balcony || 0}</span>
              <span className="text-xs text-gray-600">Balcony</span>
            </div>
            <div className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-lg sm:text-xl mb-1">📐</span>
              <span className="font-semibold text-xs sm:text-sm">{getDisplayArea(property)}</span>
              <span className="text-xs text-gray-600">Area</span>
            </div>
            <div className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg">
              <span className="text-lg sm:text-xl mb-1">🚗</span>
              <span className="font-semibold text-sm sm:text-base">{property.parkingSpaces || 0}</span>
              <span className="text-xs text-gray-600">Parking</span>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-blue-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs sm:text-sm">
              {(property.floorNo || property.totalFloors) && (
                <div>
                  <span className="text-gray-600">Floor:</span>
                  <span className="font-semibold text-gray-900 ml-1">
                    {property.floorNo || 'N/A'} / {property.totalFloors || 'N/A'}
                  </span>
                </div>
              )}
              {property.doorFacing && (
                <div>
                  <span className="text-gray-600">Facing:</span>
                  <span className="font-semibold text-gray-900 ml-1">{property.doorFacing}</span>
                </div>
              )}
              {property.availableFrom && (
                <div>
                  <span className="text-gray-600">Available:</span>
                  <span className="font-semibold text-gray-900 ml-1">{property.availableFrom}</span>
                </div>
              )}
            </div>
          </div>

          
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16 sm:pt-20">
      {/* Header Section - MOBILE OPTIMIZED */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-12 sm:py-16 md:py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6">
            Discover <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Premium Properties</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl opacity-90 max-w-3xl mx-auto leading-relaxed px-4">
            Find your perfect home from our curated collection of {pagination.totalProperties || 0} exceptional properties
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
        {/* MOBILE: Filter Button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
          {/* Filters Sidebar - MOBILE OPTIMIZED */}
          <div className={`lg:w-1/4 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Filters</h3>
                <button 
                  onClick={handleClearFilters}
                  className="text-red-600 hover:text-red-700 font-semibold text-xs sm:text-sm bg-red-50 hover:bg-red-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-all duration-300"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Search */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    🔍 Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search properties..."
                    value={filters.keyword}
                    onChange={(e) => handleFilterChange('keyword', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    📋 Listing Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  >
                    <option value="">All Status</option>
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                    <option value="rented">Rented</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Builder */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    🏗️ Builder/Developer
                  </label>
                  <input
                    type="text"
                    placeholder="Builder name"
                    value={filters.builder}
                    onChange={(e) => handleFilterChange('builder', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  />
                </div>

                {/* Property Type */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    🏠 Property Type
                  </label>
                  <select
                    value={filters.propertyType}
                    onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  >
                    <option value="">All Types</option>
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="villa">Villa</option>
                    <option value="land">Land</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>

                {/* Property Category */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    ⭐ Property Grade
                  </label>
                  <select
                    value={filters.propertyCategory}
                    onChange={(e) => handleFilterChange('propertyCategory', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  >
                    <option value="">All Grades</option>
                    <option value="A grade">A Grade</option>
                    <option value="B grade">B Grade</option>
                    <option value="C grade">C Grade</option>
                    <option value="Premium">Premium</option>
                    <option value="Luxury">Luxury</option>
                    <option value="Budget">Budget</option>
                  </select>
                </div>

                {/* Project Status */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    🏗️ Project Status
                  </label>
                  <select
                    value={filters.projectStatus}
                    onChange={(e) => handleFilterChange('projectStatus', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  >
                    <option value="">All Status</option>
                    <option value="Completed">Completed</option>
                    <option value="Under Construction">Under Construction</option>
                    <option value="Ready to move in">Ready to Move In</option>
                    <option value="Upcoming">Upcoming</option>
                  </select>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    📍 City
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city name"
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                  />
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                    💰 Price Range (₹)
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Bedrooms, Bathrooms, Balcony */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                      🛏️
                    </label>
                    <select
                      value={filters.bedrooms}
                      onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                      className="w-full px-1 sm:px-2 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-xs sm:text-sm"
                    >
                      <option value="">Any</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                      <option value="5">5+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                      🚿
                    </label>
                    <select
                      value={filters.bathrooms}
                      onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                      className="w-full px-1 sm:px-2 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-xs sm:text-sm"
                    >
                      <option value="">Any</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                      🏡
                    </label>
                    <select
                      value={filters.balcony}
                      onChange={(e) => handleFilterChange('balcony', e.target.value)}
                      className="w-full px-1 sm:px-2 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-xs sm:text-sm"
                    >
                      <option value="">Any</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                    </select>
                  </div>
                </div>

                

                {/* Advanced Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full flex items-center justify-center space-x-2 py-2.5 sm:py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-xl transition-all duration-300 text-sm sm:text-base"
                >
                  <svg 
                    className={`w-4 h-4 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span>{showFilters ? 'Less Filters' : 'More Filters'}</span>
                </button>

                {/* Advanced Filters */}
                {showFilters && (
                  <div className="space-y-4 sm:space-y-6 pt-4 sm:pt-6 border-t border-gray-200">
                    {/* Carpet Area Range */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        📐 Carpet Area (sq ft)
                      </label>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.minArea}
                          onChange={(e) => handleFilterChange('minArea', e.target.value)}
                          className="px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.maxArea}
                          onChange={(e) => handleFilterChange('maxArea', e.target.value)}
                          className="px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {/* Furnishing */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        🪑 Furnishing
                      </label>
                      <select
                        value={filters.furnishing}
                        onChange={(e) => handleFilterChange('furnishing', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                      >
                        <option value="">Any</option>
                        <option value="Fully-Furnished">Fully Furnished</option>
                        <option value="Semi-Furnished">Semi Furnished</option>
                        <option value="Bareshell">Bareshell</option>
                        <option value="Unfurnished">Unfurnished</option>
                      </select>
                    </div>

                    {/* Door Facing */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        🧭 Door Facing
                      </label>
                      <select
                        value={filters.doorFacing}
                        onChange={(e) => handleFilterChange('doorFacing', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                      >
                        <option value="">Any Direction</option>
                        <option value="North">North</option>
                        <option value="South">South</option>
                        <option value="East">East</option>
                        <option value="West">West</option>
                        <option value="North-East">North-East</option>
                        <option value="North-West">North-West</option>
                        <option value="South-East">South-East</option>
                        <option value="South-West">South-West</option>
                      </select>
                    </div>

                    {/* Katha Type */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        📄 Katha Type
                      </label>
                      <select
                        value={filters.katha}
                        onChange={(e) => handleFilterChange('katha', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                      >
                        <option value="">Any Katha</option>
                        <option value="A katha">A Katha</option>
                        <option value="B Katha">B Katha</option>
                        <option value="E katha">E Katha</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        🗓️ Year Built
                      </label>
                      <input
                        type="number"
                        placeholder="Minimum year"
                        value={filters.yearBuilt}
                        onChange={(e) => handleFilterChange('yearBuilt', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                        🚗 Parking Spaces
                      </label>
                      <input
                        type="number"
                        placeholder="Minimum spaces"
                        value={filters.parkingSpaces}
                        onChange={(e) => handleFilterChange('parkingSpaces', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                )}

                {/* MOBILE: Apply Filters Button */}
                <div className="lg:hidden pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Grid */}
          <div className="lg:w-3/4">
            {/* Results Header - MOBILE OPTIMIZED */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                    {pagination.totalProperties || 0} Properties
                  </h2>
                  {filters.city && (
                    <p className="text-gray-600 text-sm sm:text-base">in <span className="font-semibold text-blue-600">{filters.city}</span></p>
                  )}
                  {error && (
                    <p className="text-red-600 text-xs sm:text-sm bg-red-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg mt-2">❌ {error}</p>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                  {/* Sort By - MOBILE OPTIMIZED */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Sort:</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      className="flex-1 sm:flex-none px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                    >
                      <option value="date-desc">Newest</option>
                      <option value="date-asc">Oldest</option>
                      <option value="price-asc">Price: Low-High</option>
                      <option value="price-desc">Price: High-Low</option>
                      <option value="area-desc">Largest Area</option>
                      <option value="carpetArea-desc">Largest Carpet</option>
                      <option value="balcony-desc">Most Balconies</option>
                    </select>
                  </div>

                  {/* View Toggle - DESKTOP ONLY */}
                  <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'grid' 
                          ? 'bg-white shadow-md text-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'list' 
                          ? 'bg-white shadow-md text-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Pagination Info */}
                  <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                    Page {pagination.currentPage || 1} of {pagination.totalPages || 1}
                  </div>
                </div>
              </div>
            </div>

            {/* Properties Display */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="animate-pulse bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="h-48 sm:h-56 md:h-64 bg-gray-300"></div>
                    <div className="p-4 sm:p-6">
                      <div className="h-4 bg-gray-300 rounded mb-3 sm:mb-4"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4 mb-3 sm:mb-4"></div>
                      <div className="h-6 bg-gray-300 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : properties.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                    {properties.map((property) => (
                      <PropertyCard key={property.id} property={property} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {properties.map((property) => (
                      <PropertyListItem key={property.id} property={property} />
                    ))}
                  </div>
                )}

                {/* Pagination - MOBILE OPTIMIZED */}
                {pagination.totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-8 sm:mt-16 p-4 sm:p-8 bg-white rounded-2xl shadow-lg gap-4">
                    <div className="text-gray-600 text-xs sm:text-sm text-center sm:text-left">
                      Showing {((pagination.currentPage - 1) * (pagination.limit || 12)) + 1} to {Math.min(pagination.currentPage * (pagination.limit || 12), pagination.totalProperties)} of {pagination.totalProperties}
                    </div>
                    
                    <div className="flex items-center gap-1 sm:gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={!pagination.hasPrev}
                        className="px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium rounded-lg transition-all duration-300 disabled:hover:bg-gray-100 text-xs sm:text-sm"
                      >
                        Prev
                      </button>
                      
                      {[...Array(Math.min(pagination.totalPages, 3))].map((_, index) => {
                        const page = Math.max(1, Math.min(pagination.currentPage - 1, pagination.totalPages - 2)) + index;
                        if (page > pagination.totalPages) return null;
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 sm:px-4 py-2 font-medium rounded-lg transition-all duration-300 text-xs sm:text-sm ${
                              page === pagination.currentPage
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={!pagination.hasNext}
                        className="px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium rounded-lg transition-all duration-300 disabled:hover:bg-gray-100 text-xs sm:text-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 sm:py-20 bg-white rounded-2xl shadow-lg px-4">
                <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">🏠</div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-gray-900">No Properties Found</h3>
                <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">Try adjusting your search criteria</p>
                <button 
                  onClick={handleClearFilters}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl text-sm sm:text-base"
                >
                  Clear Filters & View All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Properties;
